import { useEffect, useState } from "react";
import { Plane, Bell, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import NotificationPreferences from "@/components/NotificationPreferences";
import "./HomeFlightsWidget.css";

const AIRLINE_BRAND = {
  "BOLIVIANA DE AVIACION": { color: "#e30613", logo: "" },
  "ECO JET": { color: "#1e88e5", logo: "" },
  "COPA AIRLINES": { color: "#0c2d5b", logo: "" },
  "TAM EP": { color: "#005f3d", logo: "" },
  "AMASZONAS": { color: "#ff6a00", logo: "" },
  "LATAM AIRLINES": { color: "#e30613", logo: "" },
  "AVIANCA": { color: "#e60022", logo: "" },
  "AEROLINEAS ARGENTINAS": { color: "#75aadb", logo: "" },
  "GOL": { color: "#ff6600", logo: "" },
  "AMERICAN AIRLINES": { color: "#0078d2", logo: "" },
};

function AirlineLogo({ airline, code, overrideLogo }) {
  const key = (airline || "").toUpperCase();
  const brand = AIRLINE_BRAND[key] || { color: "#374151", logo: "" };
  const logoUrl = overrideLogo || brand.logo;
  const [imgFailed, setImgFailed] = useState(false);
  if (logoUrl && !imgFailed) {
    return (
      <div className="fl-logo fl-logo-img" title={airline}>
        <img src={logoUrl} alt={airline} onError={() => setImgFailed(true)} loading="lazy" />
      </div>
    );
  }
  return (
    <div className="fl-logo fl-logo-badge" style={{ background: brand.color }} title={airline}>
      <span>{code || (airline || "").slice(0, 3)}</span>
    </div>
  );
}

export default function HomeFlightsWidget({ compact = false }) {
  const [data, setData] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [error, setError] = useState(false);
  const [showNotifs, setShowNotifs] = useState(false);
  const [logos, setLogos] = useState({});

  useEffect(() => {
    let alive = true;
    const load = () => {
      api.get("/flights/alerts", { params: { limit: 12 } })
        .then((r) => { if (alive) { setData(r.data); setError(false); } })
        .catch(() => { if (alive) setError(true); });
    };
    load();
    api.get("/settings/public").then((r) => {
      if (alive) setLogos(r.data?.airline_logos || {});
    }).catch(() => {});
    const id = setInterval(load, 5 * 60 * 1000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  if (error) return null;
  if (!data) {
    return (
      <section className={`fl-mod ${compact ? "fl-compact" : ""}`} data-testid="flights-widget-loading">
        <div className="fl-head">
          <div className="fl-icon"><Plane size={18} /></div>
          <div className="fl-titles">
            <h3>Vuelos en alerta</h3>
            <p>Solo demorados y cancelados</p>
          </div>
        </div>
        <div className="fl-skel"><span /><span /><span /></div>
      </section>
    );
  }

  const STATUS_RANK = { cancelled: 0, delayed: 1, rescheduled: 2, boarding: 3, ok: 4 };
  const items = [...(data.items || [])].sort((a, b) => {
    const ra = STATUS_RANK[a.status_class] ?? 5;
    const rb = STATUS_RANK[b.status_class] ?? 5;
    if (ra !== rb) return ra - rb;
    return (a.scheduled_iso || "").localeCompare(b.scheduled_iso || "");
  }).slice(0, 8);

  return (
    <>
    <section className={`fl-mod ${compact ? "fl-compact" : ""}`} data-testid="flights-widget">
      <div className="fl-head">
        <div className="fl-icon"><Plane size={18} /></div>
        <div className="fl-titles">
          <h3>Vuelos en alerta</h3>
          <p>Solo demorados y cancelados</p>
        </div>
        <button type="button" className="fl-bell" title="Configurar notificaciones" data-testid="flights-bell" onClick={() => setShowNotifs(true)}>
          <Bell size={16} />
          {items.length > 0 && <span className="fl-bell-dot" />}
        </button>
      </div>

      <div className="fl-list">
        {items.length === 0 ? (
          <p className="fl-empty">Sin alertas — todos los vuelos en horario ✓</p>
        ) : (
          items.map((f) => {
            const open = openId === f.id;
            const isCancelled = f.status_class === "cancelled";
            return (
              <div key={f.id} className={`fl-row ${open ? "fl-row-open" : ""} ${isCancelled ? "fl-row-cancelled" : ""}`} data-testid={`flight-${f.id}`}>
                <button type="button" onClick={() => setOpenId(open ? null : f.id)} className="fl-row-btn">
                  <AirlineLogo airline={f.airline} code={f.airline_short} overrideLogo={logos[(f.airline || "").toUpperCase()]} />
                  <div className="fl-route">
                    <p className="fl-code"><b>{f.origin_iata}</b> · {f.flight_code}</p>
                    <p className="fl-iata">{f.from_iata} <span className="fl-arrow">→</span> {f.to_iata}</p>
                  </div>
                  <div className="fl-time">{f.scheduled || "—"}</div>
                  <div className="fl-status">
                    <span className={`fl-dot fl-dot-${f.status_class}`} />
                    <span className={`fl-pill fl-pill-${f.status_class}`}>{f.status}</span>
                  </div>
                </button>
                {open && (
                  <div className="fl-detail" data-testid={`flight-detail-${f.id}`}>
                    <div className="fl-detail-row"><span>Aerolínea</span><b>{f.airline}</b></div>
                    <div className="fl-detail-row"><span>Ruta</span><b>{f.from_name} → {f.to_name}</b></div>
                    {f.gate && <div className="fl-detail-row"><span>Puerta</span><b>{f.gate}</b></div>}
                    <div className="fl-detail-row"><span>Hora estimada</span><b>{f.scheduled || "—"}</b></div>
                    <div className="fl-detail-row"><span>Hora real</span><b>{f.real || "—"}</b></div>
                    <div className="fl-detail-row"><span>Tipo</span><b>{f.tipo === "arrival" ? "Llegada" : "Salida"}</b></div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      <div className="fl-foot">
        <Link to="/vuelos" className="fl-link" data-testid="flights-link-all">
          Ver todos los vuelos <ChevronRight size={14} />
        </Link>
        <a href="https://fids.naabol.gob.bo/" target="_blank" rel="noopener noreferrer" className="fl-source" title="Datos en vivo de NAABOL">
          Fuente: NAABOL
        </a>
      </div>
    </section>
    {showNotifs && <NotificationPreferences onClose={() => setShowNotifs(false)} />}
    </>
  );
}
